/**
 * An internal link that keeps the application running.
 *
 * `@catalog/ui`'s `Link` is a styled `<a>`, which is correct — a design system should not know
 * which router its host uses. But a bare `<a href>` inside a single-page app is a full document
 * reload: the bundle is re-downloaded, the query cache is thrown away, and the session is
 * re-checked, all to move between two screens that were already loaded.
 *
 * React Router's own `useHref` and `useLinkClickHandler` are exactly the seam for this. The
 * result is a real anchor with a real `href` — so middle-click, ⌘-click and "copy link address"
 * all behave — whose plain left-click is handled in the client.
 */
import { forwardRef, type MouseEvent } from 'react';
import { useHref, useLinkClickHandler } from 'react-router';

import { Link, type LinkProps } from '@catalog/ui';

export interface AppLinkProps extends Omit<LinkProps, 'href'> {
  /** An internal path, e.g. `/courses/9780137928576`. */
  to: string;
  /** Replace the current history entry instead of pushing a new one. */
  replace?: boolean;
}

export const AppLink = forwardRef<HTMLAnchorElement, AppLinkProps>(function AppLink(
  { to, replace = false, target, onClick, ...rest },
  ref,
) {
  const href = useHref(to);
  const handleClick = useLinkClickHandler<HTMLAnchorElement>(to, {
    replace,
    ...(target === undefined ? {} : { target }),
  });

  return (
    <Link
      {...rest}
      ref={ref}
      href={href}
      {...(target === undefined ? {} : { target })}
      onClick={(event: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(event);
        // `useLinkClickHandler` already ignores modified clicks, non-primary buttons and
        // `target="_blank"`. What it does not check is whether something upstream has already
        // decided to handle this click itself.
        if (!event.defaultPrevented) handleClick(event);
      }}
    />
  );
});
